<template>
    <b-dropdown
        size="sm"
        :variant="isFilterActive ? 'primary' : 'outline-secondary'"
        no-caret
        boundary="window"
        class="grid-filter-chip filter-author-chip"
        :disabled="disabled"
    >
        <template #button-content>
            <i :class="chipIcon"></i>
            <span class="font-weight-bold mr-1">{{ label }}:</span>
            <span>{{ summaryText }}</span>
            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
        </template>

        <div class="filter-author-dropdown-menu">
            <div class="filter-author-header px-2 py-1 border-bottom text-muted small font-weight-bold d-flex justify-content-between align-items-center">
                <span>Filter by {{ label }}</span>
                <b-button
                    v-if="selectedKey !== defaultKey"
                    size="sm"
                    variant="link"
                    class="p-0 text-decoration-none text-muted filter-reset-btn"
                    @click.stop="selectAuthor(defaultKey)"
                >
                    Reset
                </b-button>
            </div>

            <div class="filter-author-list" style="max-height: 260px; overflow-y: auto;">
                <!-- 0. All Option -->
                <b-dropdown-item-button
                    v-if="showAllOption"
                    :active="selectedKey === 'all'"
                    @click="selectAuthor('all')"
                    class="filter-author-item"
                    :data-test="`${testPrefix}-option-all`"
                >
                    <div class="d-flex align-items-center w-100">
                        <i class="bi bi-people mr-2 text-secondary"></i>
                        <span class="flex-grow-1 font-weight-500">{{ allLabel }}</span>
                        <i v-if="selectedKey === 'all'" class="bi bi-check2 text-primary font-weight-bold ml-2"></i>
                    </div>
                </b-dropdown-item-button>

                <!-- 1. Combined Me (Current User) Option -->
                <b-dropdown-item-button
                    :active="selectedKey === 'me'"
                    @click="selectAuthor('me')"
                    class="filter-author-item"
                    :data-test="`${testPrefix}-option-me`"
                >
                    <div class="d-flex align-items-center w-100 text-truncate">
                        <img
                            v-if="currentUserMember && currentUserMember.imageUrl"
                            :src="currentUserMember.imageUrl"
                            class="rounded-circle mr-2 member-filter-avatar"
                            alt=""
                        />
                        <i v-else class="bi bi-person-fill mr-2 text-primary"></i>
                        <span class="flex-grow-1 text-truncate font-weight-500" :title="meOptionLabel">{{ meOptionLabel }}</span>
                        <i v-if="selectedKey === 'me'" class="bi bi-check2 text-primary font-weight-bold ml-2"></i>
                    </div>
                </b-dropdown-item-button>

                <!-- 2. Actual Team Name Option (not 'My Team') -->
                <b-dropdown-item-button
                    v-if="showTeamOption"
                    :active="selectedKey === 'team'"
                    @click="selectAuthor('team')"
                    class="filter-author-item"
                    :data-test="`${testPrefix}-option-team`"
                >
                    <div class="d-flex align-items-center w-100">
                        <i class="bi bi-people-fill mr-2 text-info"></i>
                        <span class="flex-grow-1 text-truncate font-weight-500" :title="actualTeamName">{{ actualTeamName }}</span>
                        <i v-if="selectedKey === 'team'" class="bi bi-check2 text-primary font-weight-bold ml-2"></i>
                    </div>
                </b-dropdown-item-button>

                <!-- Divider before other individual members -->
                <b-dropdown-divider v-if="loadingMembers || otherTeamMembers.length > 0" class="my-1"></b-dropdown-divider>

                <!-- Loading State -->
                <div v-if="loadingMembers" class="py-2 text-center">
                    <load-spinner size="sm" inline text="Loading members..." />
                </div>

                <!-- 3. Other Team Members (excluding current user who is combined into Me) -->
                <template v-else-if="otherTeamMembers.length > 0">
                    <b-dropdown-item-button
                        v-for="member in otherTeamMembers"
                        :key="getMemberKey(member)"
                        :active="selectedKey === getMemberKey(member)"
                        @click="selectAuthor(getMemberKey(member))"
                        class="filter-author-item"
                        :data-test="`${testPrefix}-option-member`"
                    >
                        <div class="d-flex align-items-center w-100 text-truncate">
                            <img
                                v-if="member.imageUrl"
                                :src="member.imageUrl"
                                class="rounded-circle mr-2 member-filter-avatar"
                                alt=""
                            />
                            <i v-else class="bi bi-person mr-2 text-muted"></i>
                            <span class="text-truncate flex-grow-1 mr-1" :title="member.displayName">{{ member.displayName }}</span>
                            <i v-if="selectedKey === getMemberKey(member)" class="bi bi-check2 text-primary font-weight-bold ml-2"></i>
                        </div>
                    </b-dropdown-item-button>
                </template>
            </div>
        </div>
    </b-dropdown>
</template>

<script>
export default {
    name: 'FilterAuthorDropdown',
    props: {
        value: {
            type: [String, Array],
            default: 'me'
        },
        projectId: {
            type: [String, Number],
            default: null
        },
        project: {
            type: Object,
            default: null
        },
        label: {
            type: String,
            default: 'Author'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showTeamOption: {
            type: Boolean,
            default: true
        },
        showAllOption: {
            type: Boolean,
            default: false
        },
        allLabel: {
            type: String,
            default: 'Anyone'
        },
        defaultKey: {
            type: String,
            default: 'me'
        },
        testPrefix: {
            type: String,
            default: 'filter-author'
        }
    },
    data() {
        return {
            loadingMembers: false,
            localMembers: []
        };
    },
    computed: {
        selectedKey() {
            const raw = Array.isArray(this.value)
                ? (this.value[0] || this.defaultKey)
                : (this.value || this.defaultKey);
            if (this.currentUserMember && this.getMemberKey(this.currentUserMember) === raw) {
                return 'me';
            }
            return raw;
        },
        actualTeamName() {
            if (this.project) {
                const name = this.project.sharedWorkspaceName || this.project.teamName || this.project.backlogTeamName;
                if (name && name.trim()) return name.trim();
            }
            const configuredProjects = this.$store.state.configuredProjects || [];
            if (this.projectId) {
                const matched = configuredProjects.find(p => p.id === this.projectId || p.id === parseInt(this.projectId, 10));
                if (matched) {
                    const name = matched.sharedWorkspaceName || matched.teamName || matched.backlogTeamName;
                    if (name && name.trim()) return name.trim();
                }
            }
            const globalProject = this.$store.state.selectedProjectId
                ? configuredProjects.find(p => p.id === this.$store.state.selectedProjectId || p.id === parseInt(this.$store.state.selectedProjectId, 10))
                : (configuredProjects.find(p => p.isPrimary) || configuredProjects[0]);
            if (globalProject) {
                const name = globalProject.sharedWorkspaceName || globalProject.teamName || globalProject.backlogTeamName;
                if (name && name.trim()) return name.trim();
            }
            if (this.$store.state.workspace?.name) {
                return this.$store.state.workspace.name;
            }
            return 'My Squad';
        },
        teamMembers() {
            const key = this.projectId || 'default';
            const cached = this.$store.state.teamMembersByProject?.[key];
            if (cached && cached.length > 0) {
                return cached;
            }
            return this.localMembers;
        },
        currentUserMember() {
            const acc = this.$store.state.account || {};
            const accName = (acc.fullName || acc.name || '').trim().toLowerCase();
            const accEmail = (acc.email || '').trim().toLowerCase();
            return (this.teamMembers || []).find(m => {
                if (!m) return false;
                if (m.isCurrentUser) return true;
                if (accEmail && m.uniqueName && m.uniqueName.trim().toLowerCase() === accEmail) return true;
                if (accName && m.displayName && m.displayName.trim().toLowerCase() === accName) return true;
                return false;
            }) || null;
        },
        meOptionLabel() {
            const name = (this.currentUserMember && this.currentUserMember.displayName)
                || (this.$store.state.account && (this.$store.state.account.fullName || this.$store.state.account.name))
                || '';
            const trimmed = String(name).trim();
            return trimmed ? `Me (${trimmed})` : 'Me';
        },
        otherTeamMembers() {
            const me = this.currentUserMember;
            return (this.teamMembers || []).filter(m => {
                if (!m) return false;
                if (m.isCurrentUser) return false;
                if (me && this.getMemberKey(m) === this.getMemberKey(me)) return false;
                return true;
            });
        },
        selectedMember() {
            return this.teamMembers.find(m => this.getMemberKey(m) === this.selectedKey);
        },
        isFilterActive() {
            return this.selectedKey !== this.defaultKey;
        },
        summaryText() {
            if (this.selectedKey === 'all') {
                return this.allLabel;
            }
            if (this.selectedKey === 'me') {
                return this.meOptionLabel;
            }
            if (this.selectedKey === 'team') {
                return this.actualTeamName;
            }
            if (this.selectedMember) {
                return this.selectedMember.displayName || this.selectedMember.uniqueName || this.selectedKey;
            }
            return this.selectedKey;
        },
        chipIcon() {
            if (this.selectedKey === 'all') {
                return 'bi bi-people mr-1';
            }
            if (this.selectedKey === 'team') {
                return 'bi bi-people-fill mr-1';
            }
            if (this.selectedKey === 'me') {
                return 'bi bi-person-fill mr-1';
            }
            return 'bi bi-person mr-1';
        }
    },
    watch: {
        projectId() {
            this.loadTeamMembers();
        }
    },
    mounted() {
        this.loadTeamMembers();
    },
    methods: {
        getMemberKey(member) {
            return member.uniqueName || member.id || member.displayName || '';
        },
        selectAuthor(key) {
            const out = Array.isArray(this.value) ? [key] : key;
            this.$emit('input', out);
            this.$emit('change', out);
        },
        async loadTeamMembers(force = false) {
            const key = this.projectId || 'default';
            if (!force && this.$store.state.teamMembersByProject?.[key]?.length > 0) {
                return;
            }
            try {
                this.loadingMembers = true;
                const members = await this.$store.dispatch('fetchTeamMembersAsync', {
                    projectId: this.projectId,
                    force
                });
                this.localMembers = members || [];
            } catch (err) {
                this.localMembers = [];
            } finally {
                this.loadingMembers = false;
            }
        }
    }
};
</script>

<style scoped>
.filter-author-dropdown-menu {
    min-width: 170px;
    max-width: 280px;
}
.filter-author-header {
    font-size: 0.75rem;
}
.filter-reset-btn {
    font-size: 0.72rem;
}
.filter-author-item {
    font-size: 0.82rem;
    padding: 0.3rem 0.6rem !important;
    cursor: pointer;
}
.filter-author-item:hover {
    background-color: rgba(0, 0, 0, 0.05);
}
[data-theme="dark"] .filter-author-item:hover {
    background-color: rgba(255, 255, 255, 0.08);
}
.member-filter-avatar {
    width: 18px;
    height: 18px;
    object-fit: cover;
}
.filter-author-list::-webkit-scrollbar {
    width: 5px;
}
.filter-author-list::-webkit-scrollbar-thumb {
    background-color: rgba(128, 128, 128, 0.3);
    border-radius: 3px;
}
</style>
